// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityDto,
    AvailabilityMessageType,
    ProductSettingsModel,
    WarehouseDto,
} from "@insite/client-framework/Types/ApiModels";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Check from "@insite/mobius/Icons/Check";
import Slash from "@insite/mobius/Icons/Slash";
import X from "@insite/mobius/Icons/X";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Tooltip from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xCommonUpdate } from "@plumberssupply/App/Handlers/XNG/Common";
import { CustomAppState } from "@plumberssupply/App/Store/Reducers";
import { color, mergeWithCss, xScreen } from "@plumberssupply/XDL/Utilities";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

interface OwnProps {
    productId: string;
    availability?: AvailabilityDto;
    unitOfMeasure: string;
    trackInventory: boolean;
    isProductDetailsPage?: boolean;
    failedToLoadInventory?: boolean;
    extendedStyles?: ProductAvailabilityStyles;

    // XNG-Changes
    globalInventory?: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    showInventory: state.context.showInventory,
    // XNG-Changes
    isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
    isPDP: getCurrentPage(state).type === "ProductDetailsPage",
});

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
    xCommonUpdate,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductAvailabilityStyles {
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    availabilityByWarehouseLinkGridItem?: GridItemProps;
    inventoryMessage?: InjectableCss;
    messageText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    errorWrapper?: InjectableCss;
    errorText?: TypographyPresentationProps;
    availabilityByWarehouseLink?: LinkPresentationProps;
    availabilityByWarehouseModal?: ModalPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    availabilityByWarehouseTable?: DataTableProps;
    availabilityByWarehouseTableHead?: DataTableHeadProps;
    availabilityByWarehouseTableHeader?: DataTableHeaderProps;
    availabilityByWarehouseTableBody?: DataTableBodyProps;
    availabilityByWarehouseTableRow?: DataTableRowProps;
    availabilityByWarehouseTableCell?: DataTableCellBaseProps;
    // XNG-Change
    outOfStock?: TypographyPresentationProps;
    inventoryMessageContent?: InjectableCss;
    wrapper?: InjectableCss;
}

export const productAvailabilityStyles: ProductAvailabilityStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    messageGridItem: { width: 12 },
    availabilityByWarehouseLinkGridItem: { width: 12 },
    availabilityByWarehouseModal: {
        size: 400,
        cssOverrides: {
            modalTitle: css`
                padding: 18px 18px 15px;
            `,
        },
    },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: 0 auto;
        `,
    },
    messageText: {
        size: 12,
        css: css`
            letter-spacing: 0;
            line-height: 15px;
            white-space: nowrap;
        `,
    },
    availabilityByWarehouseLink: {
        typographyProps: {
            size: 12,
            css: css`
                line-height: 15px;
            `,
        },
        css: css`
            margin-left: 18px;
            @media print {
                display: inline-block !important; // override buttons display: none !important
                margin-left: 0;
            }
        `,
    },
    realTimeText: {
        size: 12,
        weight: "bold",
        css: css`
            display: inline-block;
            text-align: left;
            min-width: 25px;

            &::after {
                overflow: hidden;
                display: inline-block;
                vertical-align: bottom;
                animation: ellipsis steps(4, end) 900ms infinite 1s;
                content: "\\2026";
                width: 0;
            }

            @keyframes ellipsis {
                to {
                    width: 1.25em;
                }
            }
        `,
    },
    errorText: {
        size: 12,
        weight: "bold",
    },
    availabilityByWarehouseTable: {
        cssOverrides: {
            header: css`
                width: 50%;
                background: ${color("common.ui11")};
                padding: 8px 12px;
                height: 34px;
                border-bottom: none;
            `,
        },
    },
    outOfStock: {
        size: 14,
        lineHeight: "22px",
        color: "common.ui8",
    },
    inventoryMessageContent: {
        css: css`
            display: flex;
            ${xScreen("lt", "xs")} {
                flex-wrap: wrap;
            }
        `,
    },
    wrapper: {
        css: css`
            line-height: 0;
        `,
    },
};

// XNG-Changes
const InventoryMessage = styled.div<{ color: string }>`
    display: flex;
    align-items: center;
    margin-bottom: 4px;
    width: 100%;
    ${IconWrapper} {
        margin-right: 6px;
    }
`;

const getAvailabilityColor = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return "common";
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return "common.ui8";
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return "warning";
    }

    return "success";
};

const getAvailabilityIcon = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return undefined;
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return Slash;
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return AlertTriangle;
    }

    return Check;
};

const showLink = (
    availability: AvailabilityDto | undefined,
    trackInventory: boolean,
    productSettings: ProductSettingsModel,
    isProductDetailsPage?: boolean,
): boolean => {
    if (
        !availability ||
        availability.messageType === AvailabilityMessageType.NoMessage ||
        !trackInventory ||
        !productSettings.showInventoryAvailability ||
        !productSettings.displayInventoryPerWarehouse
    ) {
        return false;
    }

    if (!productSettings.displayInventoryPerWarehouseOnlyOnProductDetail) {
        return true;
    }

    return !!isProductDetailsPage;
};

const ProductAvailability = ({
    productId,
    availability,
    unitOfMeasure,
    trackInventory,
    productSettings,
    isProductDetailsPage,
    getRealTimeWarehouseInventory,
    failedToLoadInventory,
    showInventory,
    extendedStyles,
    globalInventory,
    isTabletScreen,
    isPDP,
    xCommonUpdate,
}: Props) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [warehouses, setWarehouses] = useState<WarehouseDto[] | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const modalCloseHandler = () => {
        setModalIsOpen(false);
        xCommonUpdate({ key: "availabilityModalOpen", value: false });
    };
    const viewAvailabilityByWarehouseClickHandler = () => {
        setIsLoading(true);
        setModalIsOpen(true);
        xCommonUpdate({ key: "availabilityModalOpen", value: true });
        getRealTimeWarehouseInventory({
            productId,
            unitOfMeasure,
            expand: ["warehouses"],
            onComplete: result => {
                setWarehouses(result.warehouses);
                setErrorMessage(result.errorMessage);
                setIsLoading(false);
            },
        });
    };

    const color = getAvailabilityColor(productSettings, availability);
    const iconSrc = getAvailabilityIcon(productSettings, availability);

    const [styles] = useState(() => mergeWithCss(productAvailabilityStyles, extendedStyles));

    if (!showInventory) {
        return null;
    }

    if (failedToLoadInventory) {
        return (
            <StyledWrapper {...styles.errorWrapper}>
                {isTabletScreen || isPDP ? (
                    <Typography {...styles.errorText}>
                        {siteMessage("RealTimeInventory_InventoryLoadFailed")}
                    </Typography>
                ) : (
                    <Tooltip
                        text={siteMessage("RealTimeInventory_InventoryLoadFailed").toString()}
                        triggerComponent={
                            <Typography {...styles.errorText}>
                                {siteMessage("RealTimeInventory_InventoryLoadFailed")}
                            </Typography>
                        }
                    />
                )}
            </StyledWrapper>
        );
    }

    let inventoryTextComponent = <Typography {...styles.realTimeText} />;
    if (availability?.messageType !== AvailabilityMessageType.NoMessage && availability?.message?.length) {
        const availabilityMsg = availability.message.split(" at ");

        inventoryTextComponent = (
            <InventoryMessage color={color} {...styles.inventoryMessage}>
                <Icon color={color} src={iconSrc} size={12} />
                <StyledWrapper {...styles.inventoryMessageContent}>
                    <Typography
                        {...styles.messageText}
                        weight="bold"
                        color={availability?.messageType === AvailabilityMessageType.OutOfStock ? "text.main" : color}
                        data-test-selector="availabilityMessage"
                    >
                        {availabilityMsg[0]}
                    </Typography>
                    {availabilityMsg[1] && (
                        <>
                            <Typography
                                {...styles.messageText}
                                style={{
                                    margin: "0 4px",
                                }}
                                data-test-selector="availabilityMessageWarehouse"
                            >
                                {translate("at")}
                            </Typography>
                            <Typography
                                {...styles.messageText}
                                weight="bold"
                                data-test-selector="availabilityMessageWarehouse"
                            >
                                {availabilityMsg[1]}
                            </Typography>
                        </>
                    )}
                </StyledWrapper>
            </InventoryMessage>
        );
    } else if (availability) {
        inventoryTextComponent = <></>;
    }

    return (
        <StyledWrapper {...styles.container}>
            {inventoryTextComponent}
            {showLink(availability, trackInventory, productSettings, isProductDetailsPage) && globalInventory && (
                <StyledWrapper {...styles.wrapper}>
                    {isTabletScreen || isPDP ? (
                        <Link
                            {...styles.availabilityByWarehouseLink}
                            color={globalInventory.toLocaleLowerCase().indexOf("none") === 0 ? "text" : "secondary"}
                            onClick={viewAvailabilityByWarehouseClickHandler}
                            data-test-selector="availabilityByWarehouseLink"
                        >
                            {globalInventory}
                        </Link>
                    ) : (
                        <Tooltip
                            text={globalInventory}
                            triggerComponent={
                                <Link
                                    {...styles.availabilityByWarehouseLink}
                                    color={
                                        globalInventory.toLocaleLowerCase().indexOf("none") === 0 ? "text" : "secondary"
                                    }
                                    onClick={viewAvailabilityByWarehouseClickHandler}
                                    data-test-selector="availabilityByWarehouseLink"
                                >
                                    {globalInventory}
                                </Link>
                            }
                        />
                    )}
                    <Modal
                        {...styles.availabilityByWarehouseModal}
                        headline={translate("Warehouse Inventory")}
                        isOpen={modalIsOpen}
                        handleClose={modalCloseHandler}
                    >
                        {isLoading && (
                            <StyledWrapper {...styles.centeringWrapper}>
                                <LoadingSpinner {...styles.spinner} />
                            </StyledWrapper>
                        )}
                        {!isLoading && (
                            <>
                                {errorMessage ? (
                                    <div>{errorMessage}</div>
                                ) : (
                                    <DataTable
                                        {...styles.availabilityByWarehouseTable}
                                        data-test-selector="availabilityByWarehouseModal"
                                    >
                                        <DataTableHead {...styles.availabilityByWarehouseTableHead}>
                                            <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                {translate("Warehouse")}
                                            </DataTableHeader>
                                            <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                {translate("Quantity")}
                                            </DataTableHeader>
                                        </DataTableHead>
                                        <DataTableBody {...styles.availabilityByWarehouseTableBody}>
                                            {warehouses?.map(warehouse => (
                                                <DataTableRow
                                                    {...styles.availabilityByWarehouseTableRow}
                                                    key={warehouse.name}
                                                >
                                                    <DataTableCell
                                                        {...styles.availabilityByWarehouseTableCell}
                                                        data-test-selector="warehouseAvailabilityModal_Description"
                                                    >
                                                        {warehouse.description || warehouse.name}
                                                    </DataTableCell>
                                                    <DataTableCell
                                                        {...styles.availabilityByWarehouseTableCell}
                                                        data-test-selector="warehouseAvailabilityModal_Quantity"
                                                    >
                                                        {warehouse.qty || (
                                                            <Typography {...styles.outOfStock}>
                                                                {translate("Out of Stock")}
                                                            </Typography>
                                                        )}
                                                    </DataTableCell>
                                                </DataTableRow>
                                            ))}
                                        </DataTableBody>
                                    </DataTable>
                                )}
                            </>
                        )}
                    </Modal>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductAvailability);
